import React, { useEffect } from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import PrivacyContent from './PrivacyContent';
import PageSEO from './PageSEO'; // Import the SEO component

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // SEO configuration for privacy policy page
  const privacySEO = {
    title: "Privacy Policy | Kinder's Meats Deli BBQ & Catering",
    description: "Privacy Policy for Kinder's Meats Deli BBQ & Catering. Learn how we collect, use, and protect your personal information when you visit our restaurants, use our website, or engage with our services.",
    keywords: [
      "Kinder's Meats privacy policy",
      "restaurant privacy policy",
      "personal information protection",
      "data privacy",
      "customer information security",
      "website privacy policy",
      "restaurant data collection",
      "privacy rights California"
    ],
    canonicalUrl: "/privacy-policy",
    noIndex: false, // Privacy policies should be indexed for compliance
    ogImage: "https://kindersmeats.com/images/kinders-logo-og.jpg",
    customMeta: {
      'document:type': 'Legal Document',
      'document:category': 'Privacy Policy',
      'document:effective-date': 'February 9, 2026',
      'robots': 'index, follow, noarchive' // Don't cache sensitive legal content
    },
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://kindersmeats.com/privacy-policy#webpage",
          "url": "https://kindersmeats.com/privacy-policy",
          "name": "Privacy Policy | Kinder's Meats Deli BBQ & Catering",
          "description": "Privacy Policy for Kinder's Meats Deli BBQ & Catering detailing how we handle customer information and protect privacy.",
          "isPartOf": {
            "@id": "https://kindersmeats.com/#website"
          },
          "about": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "datePublished": "2025-06-04",
          "dateModified": "2026-02-09",
          "inLanguage": "en-US",
          "breadcrumb": {
            "@id": "https://kindersmeats.com/privacy-policy#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://kindersmeats.com/privacy-policy#privacypolicy"
          }
        },
        {
          "@type": "DigitalDocument",
          "@id": "https://kindersmeats.com/privacy-policy#privacypolicy",
          "name": "Kinder's Meats Privacy Policy",
          "description": "Legal document outlining privacy practices and data protection policies",
          "dateCreated": "2025-06-04",
          "dateModified": "2026-02-09",
          "author": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "publisher": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "genre": "Legal Document",
          "keywords": "privacy policy, data protection, personal information, customer privacy",
          "inLanguage": "en-US",
          "copyrightHolder": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "license": "All rights reserved",
          "about": [
            {
              "@type": "Thing",
              "name": "Data Collection",
              "description": "Information about how customer data is collected"
            },
            {
              "@type": "Thing", 
              "name": "Information Use",
              "description": "How collected information is used by the business"
            },
            {
              "@type": "Thing",
              "name": "Data Protection",
              "description": "Measures taken to protect customer information"
            },
            {
              "@type": "Thing",
              "name": "Customer Rights",
              "description": "Rights customers have regarding their personal information"
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://kindersmeats.com/#organization",
          "name": "Kinder's Meats Deli BBQ & Catering",
          "url": "https://kindersmeats.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://kindersmeats.com/images/kinders-logo.png",
            "width": 300,
            "height": 100
          },
          "description": "Family-owned BBQ restaurant chain serving authentic barbecue across 7 California locations since 1946.",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "CA",
            "addressCountry": "US"
          },
          "privacyPolicy": "https://kindersmeats.com/privacy-policy",
          "publishingPrinciples": "https://kindersmeats.com/privacy-policy"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://kindersmeats.com/privacy-policy#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://kindersmeats.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Privacy Policy",
              "item": "https://kindersmeats.com/privacy-policy"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What personal information do you collect?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We collect information you provide when visiting our restaurants, using our website, placing orders, or contacting us. This may include contact details, order preferences, and payment information necessary for service."
              }
            },
            {
              "@type": "Question",
              "name": "How do you use my personal information?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We use your information to provide restaurant services, process orders, communicate with you about your experience, and improve our services. We do not sell personal information to third parties."
              }
            },
            {
              "@type": "Question",
              "name": "How do you protect my information?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We implement appropriate security measures to protect your personal information, including secure data storage and limited access protocols. We regularly review our security practices."
              }
            },
            {
              "@type": "Question",
              "name": "Can I request deletion of my personal information?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, you have the right to request deletion of your personal information, subject to certain legal and business requirements. Contact us to make such requests."
              }
            },
            {
              "@type": "Question",
              "name": "Do you share information with third parties?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We only share information with service providers necessary to operate our business (payment processors, delivery services, etc.) and as required by law. We do not sell customer data."
              }
            }
          ]
        }
      ]
    }
  };

  return (
    <>
      {/* SEO Component */}
      <PageSEO {...privacySEO} />
      
      {/* Main content with semantic HTML structure */}
      <main>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-start'>
            <Navigation 
              colorMode="colorWhite"
            />

            {/* Privacy Policy Content */}
            <article itemScope itemType="https://schema.org/DigitalDocument">
              <PrivacyContent 
                className="content-space-160 is-10-mobile is-10-tablet is-10-desktop is-8-widescreen is-8-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-1-desktop is-offset-2-widescreen is-offset-2-fullhd"
                title="Privacy Policy"
                effectiveDate="February 9, 2026"
              />
            </article>

            {/* Hidden FAQ Section for SEO */}
            <section className="faq-section" style={{ display: 'none' }} aria-hidden="true" itemScope itemType="https://schema.org/FAQPage">
              <h2>Privacy Policy FAQ</h2>
              
              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What personal information do you collect?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    We collect information you provide when visiting our restaurants, using our website, placing orders, or contacting us. This may include contact details, order preferences, and payment information necessary for service.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">How do you use my personal information?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    We use your information to provide restaurant services, process orders, communicate with you about your experience, and improve our services. We do not sell personal information to third parties.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">How do you protect my information?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    We implement appropriate security measures to protect your personal information, including secure data storage and limited access protocols. We regularly review our security practices.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">Can I request deletion of my personal information?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Yes, you have the right to request deletion of your personal information, subject to certain legal and business requirements. Contact us to make such requests.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">Do you share information with third parties?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    We only share information with service providers necessary to operate our business (payment processors, delivery services, etc.) and as required by law. We do not sell customer data.
                  </div>
                </div>
              </div>
            </section>
            
            <Footer />
          </div>
        </section>
      </main>
    </>
  );
};

export default PrivacyPolicy;